// Service for managing user sessions with cookie persistence
import Cookies from 'js-cookie';

export interface SessionData {
  token: string;
  userId: string;
  email?: string;
  name?: string;
  expiresAt: string;
}

class SessionService {
  private readonly SESSION_COOKIE_NAME = 'jobometry_session';
  private readonly SESSION_DURATION_DAYS = 7; // Session lasts 7 days

  // Initialize session from cookie on app load
  initializeSession(): SessionData | null {
    try {
      const sessionCookie = Cookies.get(this.SESSION_COOKIE_NAME);
      if (!sessionCookie) {
        return null;
      }

      const sessionData: SessionData = JSON.parse(sessionCookie);
      
      // Check if session is expired
      if (new Date(sessionData.expiresAt) < new Date()) {
        this.clearSession();
        return null;
      }

      // Store token in localStorage for backward compatibility
      localStorage.setItem('jobometry_auth_token', sessionData.token);
      
      return sessionData;
    } catch (error) {
      console.error('Error initializing session:', error);
      this.clearSession();
      return null;
    }
  }

  // Create a new session
  createSession(token: string, userData: { userId: string; email?: string; name?: string }): SessionData {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + this.SESSION_DURATION_DAYS);

    const sessionData: SessionData = {
      token,
      userId: userData.userId,
      email: userData.email,
      name: userData.name,
      expiresAt: expiresAt.toISOString()
    };

    // Store in secure, httpOnly cookie
    Cookies.set(this.SESSION_COOKIE_NAME, JSON.stringify(sessionData), {
      expires: this.SESSION_DURATION_DAYS,
      secure: window.location.protocol === 'https:',
      sameSite: 'strict'
    });

    // Also store token in localStorage for backward compatibility
    localStorage.setItem('jobometry_auth_token', token);

    return sessionData;
  }

  // Get current session
  getSession(): SessionData | null {
    try {
      const sessionCookie = Cookies.get(this.SESSION_COOKIE_NAME);
      if (!sessionCookie) {
        return null;
      }

      const sessionData: SessionData = JSON.parse(sessionCookie);
      
      // Check if session is expired
      if (new Date(sessionData.expiresAt) < new Date()) {
        this.clearSession();
        return null;
      }

      return sessionData;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  }

  // Update session (extend expiration)
  refreshSession(): boolean {
    const currentSession = this.getSession();
    if (!currentSession) {
      return false;
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + this.SESSION_DURATION_DAYS);
    currentSession.expiresAt = expiresAt.toISOString();

    Cookies.set(this.SESSION_COOKIE_NAME, JSON.stringify(currentSession), {
      expires: this.SESSION_DURATION_DAYS,
      secure: window.location.protocol === 'https:',
      sameSite: 'strict'
    });

    return true;
  }

  // Clear session
  clearSession(): void {
    Cookies.remove(this.SESSION_COOKIE_NAME);
    localStorage.removeItem('jobometry_auth_token');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const session = this.getSession();
    return session !== null;
  }

  // Get auth token
  getAuthToken(): string | null {
    const session = this.getSession();
    return session?.token || null;
  }

  // Get user ID
  getUserId(): string | null {
    const session = this.getSession();
    return session?.userId || null;
  }
}

export const sessionService = new SessionService();